import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import { ptBR } from '@material-ui/core/locale';
import { alpha } from '@material-ui/core/styles';

export const colors = {
  white: '#FFFFFF',
  backgroundLight: '#F2F3F5',
  backgroundDefault: '#E5E7EB',
  borderDefault: '#E0E0E0',
  textSecondary: '#565A6E',
  navigationIndicator: '#E5E4E2',
  primaryDark: '#0D2240',
  primaryMain: '#015CA8',
  secondaryMain: '#00BFFF',
  secondaryHover: '#0099CC',
  accentDark: '#1F2D3D',
  warningMain: '#FFC542',
  successMain: '#1FB800',
  successContrast: '#FF5A1F',
  errorMain: '#931F1D',
  get whiteAlpha10() { return alpha(this.white, 0.1); },
  get primaryDarkAlpha4() { return alpha(this.primaryDark, 0.04); },
  get secondaryMainAlpha4() { return alpha(this.secondaryMain, 0.04); },
  get blackAlpha10() { return alpha('#000000', 0.1); },
  get blackAlpha20() { return alpha('#000000', 0.2); },
};

export const myTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: colors.primaryMain,
        contrastText: colors.white,
      },
      secondary: {
        main: colors.primaryMain,
        contrastText: colors.primaryDark,
      },
      error: {
        main: colors.errorMain,
        contrastText: colors.white,
      },
      warning: {
        main: colors.warningMain,
        contrastText: colors.primaryDark,
      },
      success: {
        main: colors.successMain,
        contrastText: colors.successContrast,
      },
      background: {
        default: colors.backgroundDefault,
        paper: colors.white,
      },
      text: {
        primary: colors.primaryDark,
        secondary: colors.textSecondary,
      },
      banner: {
        info: colors.warningMain,
        error: colors.errorMain,
        text: colors.white,
        link: colors.backgroundLight,
      },
      navigation: {
        background: colors.primaryMain,
        indicator: colors.navigationIndicator,
        color: colors.white,
        selectedColor: colors.white,
        navItem: {
          hoverBackground: colors.primaryDark,
        },
      },
    },
  }),
  fontFamily: 'Poppins, Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.wave,
      options: {
        fontColor: colors.white,
      },
    }),
    apis: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.wave,
      options: {
        fontColor: colors.white,
      },
    }),
    documentation: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.wave2,
    }),
    create: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.round,
    }),
    other: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [colors.primaryMain, colors.navigationIndicator],
      shape: shapes.wave,
    }),
  },
  components: {
    MuiTablePagination: {
      defaultProps: {
        ...ptBR.props?.MuiTablePagination,
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primaryDark,
          color: colors.white,
          boxShadow: 'none',
          borderBottom: `4px solid ${colors.primaryDark}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '80px',
          padding: '0 24px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: colors.primaryDark,
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.primaryMain,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondaryMain,
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.primaryDark,
          },
        },
        outlinedPrimary: {
          borderColor: colors.primaryDark,
          color: colors.primaryDark,
          '&:hover': {
            borderColor: colors.primaryMain,
            backgroundColor: colors.primaryDarkAlpha4,
          },
        },
        outlinedSecondary: {
          borderColor: colors.secondaryMain,
          color: colors.secondaryMain,
          '&:hover': {
            borderColor: colors.secondaryHover,
            backgroundColor: colors.secondaryMainAlpha4,
          },
        },
        root: {
          borderRadius: '8px',
          padding: '8px 16px',
          textTransform: 'uppercase',
          fontWeight: 'bold',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderRadius: '8px',
          boxShadow: `0 4px 12px ${colors.blackAlpha10}`,
          border: `1px solid ${colors.borderDefault}`,
          padding: '16px',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: `0 6px 16px ${colors.blackAlpha20}`,
          },
          color: colors.primaryDark,
          boxSizing: 'border-box',
          maxWidth: '100%',
          overflow: 'hidden',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '8px',
          justifyContent: 'center',
          '& .MuiButton-root': {
            color: colors.primaryDark,
            borderColor: colors.secondaryMain,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        placeholder: 'Digite para filtrar...',
      },
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          padding: '6px 0px',
          boxSizing: 'content-box',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          padding: '6px 0px',
          height: 'auto',
          boxSizing: 'content-box',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          padding: '6px 0px !important',
          height: 'auto',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          maxWidth: '100%',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          boxSizing: 'border-box',
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      },
    },
  },
});
